import { Add, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, GreenText, OrderableTableCell } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getToDoList as getToDoListAPI } from 'controllers/toDoLists/conformity/Approaching120DaysSinceEntryDateController'
import { VehicleDto } from 'dtos'
import { ApproachingNDaysSinceEntryDateDto, ApproachingNDaysSinceEntryDateQueryParams } from 'dtos/toDoLists/conformity'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import { formatDate, formatDateMoment, useDebounce } from 'utils'

export default function VehiclesEligibleForConformityToDoList() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [isGettingToDoList, setIsGettingToDoList] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<ApproachingNDaysSinceEntryDateQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'entryDate' })
  const [toDoList, setToDoList] = useState<ApproachingNDaysSinceEntryDateDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: ApproachingNDaysSinceEntryDateQueryParams) => {
    setIsGettingToDoList(true)
    getToDoListAPI(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        setIsGettingToDoList(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingToDoList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGettingToDoList])

  useEffect(() => {
    layout.setTitle('Approaching 120 Days Since Entry Date')
    return () => layout.setTitle('')
  }, [layout])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='entryDate'>Entry Date</MenuItem>

                    <MenuItem value='entryNumber'>Entry #</MenuItem>

                    <MenuItem value='papsNumber'>PAPS #</MenuItem>

                    <MenuItem value='inboundTripNumber'>Inbound Trip #</MenuItem>

                    <MenuItem value='vehicleCheckInDateTime'>Vehicle Check-In Completed Date</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'entryNumber', 'papsNumber', 'inboundTripNumber', 'importerStockNumber', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['entryDate', 'vehicleCheckInDateTime'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField || (['entryDate', 'vehicleCheckInDateTime'].includes(filterField) ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'entryDate') {
                        setQueryParams({
                          ...queryParams,
                          entryDateFrom: filterValueFrom,
                          entryDateTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleCheckInDateTime') {
                        setQueryParams({
                          ...queryParams,
                          vehicleCheckInDateTimeFrom: filterValueFrom,
                          vehicleCheckInDateTimeTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.entryDateFrom ||
                          queryParams.entryDateTo ||
                          queryParams.entryNumber ||
                          queryParams.papsNumber ||
                          queryParams.inboundTripNumber ||
                          queryParams.vehicleCheckInDateTimeFrom ||
                          queryParams.vehicleCheckInDateTimeTo ||
                          queryParams.importerStockNumber ||
                          queryParams.vin ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        entryDateFrom: undefined,
                        entryDateTo: undefined,
                        entryNumber: undefined,
                        papsNumber: undefined,
                        inboundTripNumber: undefined,
                        vehicleCheckInDateTimeFrom: undefined,
                        vehicleCheckInDateTimeTo: undefined,
                        importerStockNumber: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.entryDateFrom && queryParams.entryDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Entry Date from "${moment(queryParams.entryDateFrom).format('M/D/yyyy')}" to "${moment(queryParams.entryDateTo).format(
                      'M/D/yyyy'
                    )}"`}
                    onDelete={() => setQueryParams({ ...queryParams, entryDateFrom: undefined, entryDateTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.entryNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Entry # contains "${queryParams.entryNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, entryNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.papsNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`PAPS # contains "${queryParams.papsNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, papsNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.inboundTripNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Inbound Trip # contains "${queryParams.inboundTripNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, inboundTripNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vehicleCheckInDateTimeFrom && queryParams.vehicleCheckInDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Check-In Completed Date from "${moment(queryParams.vehicleCheckInDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.vehicleCheckInDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vehicleCheckInDateTimeFrom: undefined, vehicleCheckInDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${queryParams.vin}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='entryDate'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Entry Date
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='entryNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Entry #
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='papsNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              PAPS #
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='inboundTripNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Inbound Trip #
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='vehicleCheckInDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Vehicle Check-In Completed Date
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='importerStockNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Importer Stock #
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='vin'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              VIN
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='year'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='make'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<ApproachingNDaysSinceEntryDateDto>
                              keyName='model'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ApproachingNDaysSinceEntryDateDto | undefined}
                            >
                              Model
                            </OrderableTableCell>

                            <TableCell padding='checkbox' size='small'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.entryDate && moment().diff(moment(formatDate(item.entryDate)), 'days') >= 106 ? theme.palette.error.main : 'inherit',
                                  fontWeight: item.entryDate && moment().diff(moment(formatDate(item.entryDate)), 'days') >= 106 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.entryDate ? formatDateMoment(item.entryDate) : '-'}
                              </TableCell>

                              <TableCell>{item.entryNumber ?? '-'}</TableCell>

                              <TableCell>{item.papsNumber ?? '-'}</TableCell>

                              <TableCell>
                                {item.inboundTripGuid ? (
                                  <Link component={ReactRouterLink} to={`/inbound-trips/${encodeURIComponent(item.inboundTripGuid)}`}>
                                    {item.inboundTripNumber}
                                  </Link>
                                ) : (
                                  '-'
                                )}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.vehicleCheckInDateTime && moment().diff(moment(item.vehicleCheckInDateTime), 'days') >= 106
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    item.vehicleCheckInDateTime && moment().diff(moment(item.vehicleCheckInDateTime), 'days') >= 106 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.vehicleCheckInDateTime ? formatDateMoment(item.vehicleCheckInDateTime) : '-'}
                              </TableCell>

                              <TableCell>{item.importerStockNumber ?? '-'}</TableCell>

                              <TableCell>{item.vin}</TableCell>

                              <TableCell>{item.year}</TableCell>

                              <TableCell>{item.make}</TableCell>

                              <TableCell>{item.model}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => navigate(`/vehicles/${encodeURIComponent(item.vehicleGuid)}/conformity`)}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Approaching 120 Days Since Entry Date  To Do List**

Vehicles appear in this To Do List when vehicle is checked in, current date is ≥ Entry Date + 100 days, and “Sent to DOT” date field is blank under Vehicle Details – Conformity (tab). Entry Date reads from the Inbound Trip the vehicle is associated with. Inbound Trip value is a link that navigates users to the Inbound Trip Details page for that Inbound Trip #.

The expected use case for this screen is that users will see any vehicles listed here, navigate to the “Conformity” feature, and then perform the needed steps to submit a Conformity zip to the DOT. In effect, users should keep this “to do list” empty at all times.

This list is sorted by Created Date (oldest to newest).

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Entry Date,
   - Entry #,
   - PAPS #,
   - Inbound Trip #
   - Vehicle Check-In Completed Date,
   - Importer Stock #,
   - VIN,
   - Year,
   - Make,
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Entry Date: Displays Filter Date From and Filter Date To.
   - Entry #: Displays a free-type filter field.
   - PAPS #: Displays a free-type filter field.
   - Inbound Trip #: Displays a free-type filter field.
   - Vehicle Check-In Completed Date: Displays Filter Date From and Filter Date To.
   - Importer Stock # a free-type filter field.
   - VIN: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Entry Date Column:**

Entry Date is bold and red when current date is ≥ Entry Date + 106 days.

**Vehicle Check-In Date Completed Column:**

Vehicle Check-In Completed value will be in bold and red if current date is ≥ Vehicle Checked-In Date + 106 days. “Vehicle Checked-In Date” is the date the first vehicle check-in image was associated with the vehicle.

**Edit Action:**

Clicking the "EDIT" text button navigates the user to Vehicle Details – Conformity (tab) for that vehicle.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
